import React from "react";
import { useTranslation } from "react-i18next";
import Seo from "../seo/Seo";
import CtaPair from "./CtaPair";
import TodoText from "./TodoText";
import { SITE_URL } from "../seo/site";
import "../styles/Offers.css";

// The three offer detail pages differ in which sections they have and in what
// order, not in how a section looks. Each page passes its locale namespace and
// its section order; everything else is shared so the three stay consistent
// and so EN/HU/SR keep the same shape.

const TermList = ({ items, numbered = false }) => (
  <ol className={`offer-terms ${numbered ? "offer-terms--numbered" : "offer-terms--plain"}`}>
    {items.map((item, i) => (
      <li key={i}>
        {numbered && <span className="offer-term-number" aria-hidden="true">{i + 1}</span>}
        <div>
          <strong className="offer-term">{item.term}</strong>
          <span className="offer-term-text"> — <TodoText>{item.text}</TodoText></span>
        </div>
      </li>
    ))}
  </ol>
);

const Prose = ({ body }) => (body || []).map((para, i) => <p key={i}><TodoText>{para}</TodoText></p>);

const SECTIONS = {
  whatItIs: (s) => (
    <>
      <Prose body={s.body} />
      {s.items && <TermList items={s.items} />}
    </>
  ),
  forWho: (s) => (
    <>
      {s.intro && <p>{s.intro}</p>}
      {s.items && <TermList items={s.items} />}
      {s.note && <p className="offer-note">{s.note}</p>}
    </>
  ),
  steps: (s) => <TermList items={s.items} numbered />,
  gets: (s) => (
    <>
      <Prose body={s.body} />
      <TermList items={s.items} />
    </>
  ),
  after: (s) => (
    <>
      <Prose body={s.body} />
      {s.items && <TermList items={s.items} />}
    </>
  ),
  aiAct: (s) => (
    <>
      <Prose body={s.body} />
      {s.note && <p className="offer-disclaimer">{s.note}</p>}
    </>
  ),
  formats: (s) => (
    <div className="offer-formats">
      {s.items.map((f, i) => (
        <div className="offer-format" key={i}>
          <h3>{f.name}</h3>
          <p className="offer-format-price">{f.price}</p>
          <p className="offer-format-duration">{f.duration}</p>
          <p className="offer-format-text">{f.text}</p>
        </div>
      ))}
    </div>
  ),
  faq: (s) => (
    <div className="offer-faq">
      {s.items.map((item, i) => (
        <details className="offer-faq-item" key={i}>
          <summary>{item.q}</summary>
          <p><TodoText>{item.a}</TodoText></p>
        </details>
      ))}
    </div>
  ),
};

const OfferPage = ({ ns, page, path, order }) => {
  const { t } = useTranslation();
  const data = t(ns, { returnObjects: true });

  const faqJsonLd = data.faq
    ? {
        "@type": "FAQPage",
        "@id": `${SITE_URL}${path}#faq`,
        mainEntity: data.faq.items.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }
    : null;

  const offerJsonLd = {
    "@type": "Service",
    "@id": `${SITE_URL}${path}#service`,
    name: data.title,
    description: data.lead,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: ["Serbia", "Hungary", "European Union"],
    availableLanguage: ["en", "hu", "sr"],
  };

  return (
    <div className="offer-page">
      <Seo
        page={page}
        path={path}
        override={{ jsonLd: faqJsonLd ? [offerJsonLd, faqJsonLd] : [offerJsonLd] }}
      />

      <section className="offer-hero">
        <div className="container">
          <span className="offer-eyebrow">{data.eyebrow}</span>
          <h1>{data.title}</h1>
          <p className="offer-lead">{data.lead}</p>
          <div className="offer-price">
            <span className="offer-price-value">{data.price.value}</span>
            <span className="offer-price-note">{data.price.note}</span>
          </div>
          <CtaPair tone="dark" />
        </div>
      </section>

      <section className="offer-body">
        <div className="container">
          {order.map((key) => {
            const section = data[key];
            if (!section || !SECTIONS[key]) return null;
            return (
              <div className={`offer-section offer-section--${key}`} key={key}>
                <h2>{section.title}</h2>
                {SECTIONS[key](section)}
              </div>
            );
          })}
        </div>
      </section>

      <section className="offer-closing">
        <div className="container">
          <h2>{data.closing.title}</h2>
          <p>{data.closing.text}</p>
          <CtaPair tone="dark" align="center" />
        </div>
      </section>
    </div>
  );
};

export default OfferPage;
